// Copyright 2026 The OpenAgent Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import * as Setting from "../Setting";

// Module-level cache of resolved user info keyed by user name. The value is the
// in-flight (or settled) Promise so that many UserLabel instances asking for the
// same user share a single request. Resolved shape: {name, displayName, avatar}.
const userInfoCache = new Map();

function fetchUserInfo(name) {
  return fetch(`${Setting.ServerUrl}/api/get-user-info?name=${encodeURIComponent(name)}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  })
    .then(res => Setting.handleFetchResponse(res))
    .then((res) => {
      if (res.status === "ok" && res.data) {
        return res.data;
      }
      // Unresolved (e.g. not signed in yet, or a transient error) — drop the
      // cache entry so a later lookup retries instead of being stuck rendering
      // the raw username for the rest of the page's lifetime.
      userInfoCache.delete(name);
      return {name};
    })
    .catch(() => {
      userInfoCache.delete(name);
      return {name};
    });
}

// getUserInfo returns a Promise that resolves to {name, displayName, avatar}.
// Results are memoized for the lifetime of the page so repeated lookups of the
// same user (e.g. across insights cards or comment threads) are free.
export function getUserInfo(name) {
  if (!name) {
    return Promise.resolve({name: ""});
  }
  if (!userInfoCache.has(name)) {
    userInfoCache.set(name, fetchUserInfo(name));
  }
  return userInfoCache.get(name);
}
